import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import {
  CodeAgentSession,
  CodeAuditLog,
  CodeAgentStats,
  CodeAgentKillSwitchSettings,
  CodeAgentToolOwner,
  CodeAgentToolRuntime,
  CodeAgentToolVisibility,
  CodeAgentToolVisibilityMeta,
  CodeAgentToolVisibilityStatus,
  CodeAgentRiskLevel,
} from '../types/code-agent';
import { AxiosError } from 'axios';
import { useNotification } from '../hooks/useNotification';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  meta?: any;
  message?: string;
  error?: string;
}

export type CodeAgentToolFilters = {
  status?: CodeAgentToolVisibilityStatus | 'all';
  runtime?: CodeAgentToolRuntime;
  owner?: CodeAgentToolOwner;
  risk_level?: CodeAgentRiskLevel;
};

export function useCodeAgentSessions(page: number = 1, pageSize: number = 20, status?: string) {
  return useQuery({
    queryKey: ['code-agent-sessions', page, pageSize, status],
    queryFn: async () => {
      const params = new URLSearchParams({ page: page.toString(), limit: pageSize.toString() });
      if (status) params.append('status', status);
      const response = await axiosInstance.get<ApiResponse<CodeAgentSession[]>>(`/v1/admin/code-agent/sessions?${params.toString()}`);
      return response.data;
    },
  });
}

export type CodeAgentTurnMetricsRow = {
  turnId: string;
  mode: string;
  state: string;
  createdAt: string;
  updatedAt: string;
  metrics: Record<string, unknown> | null;
};

export type CodeAgentTurnMetricsTotals = {
  turns: number;
  turnsWithMetrics: number;
  promptTokens: number;
  completionTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  cacheReadRatio: number | null;
  retryTotal: number;
  iterations: number;
};

/** Spec 01 §0: per-turn token/cache/retry metrics of one session. */
export function useCodeAgentTurnMetrics(sessionPublicId: string | null) {
  return useQuery({
    queryKey: ['code-agent-turn-metrics', sessionPublicId],
    enabled: Boolean(sessionPublicId),
    queryFn: async () => {
      const response = await axiosInstance.get<
        ApiResponse<CodeAgentTurnMetricsRow[]> & { totals?: CodeAgentTurnMetricsTotals }
      >(`/v1/admin/code-agent/sessions/${sessionPublicId}/turn-metrics?limit=100`);
      return { rows: response.data.data ?? [], totals: response.data.totals ?? null };
    },
  });
}

export function useKillSession() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axiosInstance.post<ApiResponse>(`/v1/admin/code-agent/sessions/${id}/kill`);
      return response.data;
    },
    onSuccess: () => {
      notification.success('Session killed successfully');
      queryClient.invalidateQueries({ queryKey: ['code-agent-sessions'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.message || error.message || 'Failed to kill session';
      notification.error(`Kill error: ${message}`);
    },
  });
}

export type CodeAuditLogFilters = {
  eventType?: string;
  state?: string;
  source?: string;
  turnId?: string;
  actionId?: string;
  recoveryIncidentId?: string;
};

export function useCodeAuditLogs(
  page: number = 1,
  pageSize: number = 20,
  filters: CodeAuditLogFilters = {},
) {
  return useQuery({
    queryKey: ['code-audit-logs', page, pageSize, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ page: page.toString(), limit: pageSize.toString() });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await axiosInstance.get<ApiResponse<CodeAuditLog[]>>(`/v1/admin/code-agent/audit-logs?${params.toString()}`);
      return response.data;
    },
  });
}

export function useCodeAgentTools(
  page: number = 1,
  pageSize: number = 50,
  filters: CodeAgentToolFilters = {},
) {
  return useQuery({
    queryKey: ['code-agent-tools', page, pageSize, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ page: page.toString(), limit: pageSize.toString() });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await axiosInstance.get<
        ApiResponse<CodeAgentToolVisibility[]> & { meta?: CodeAgentToolVisibilityMeta }
      >(`/v1/admin/code-agent/tools?${params.toString()}`);
      return response.data;
    },
  });
}

export function useCodeAgentStats() {
  return useQuery({
    queryKey: ['code-agent-stats'],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<CodeAgentStats>>('/v1/admin/code-agent/stats');
      return response.data;
    },
  });
}

export function useCodeAgentKillSwitches() {
  return useQuery({
    queryKey: ['code-agent-kill-switches'],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<CodeAgentKillSwitchSettings>>('/v1/admin/code-agent/kill-switches');
      return response.data;
    },
  });
}

export function useUpdateCodeAgentKillSwitches() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (settings: CodeAgentKillSwitchSettings) => {
      const response = await axiosInstance.put<ApiResponse<CodeAgentKillSwitchSettings>>('/v1/admin/code-agent/kill-switches', settings);
      return response.data;
    },
    onSuccess: () => {
      notification.success('Kill switch settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['code-agent-kill-switches'] });
      queryClient.invalidateQueries({ queryKey: ['code-audit-logs'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.message || error.message || 'Failed to update kill switches';
      notification.error(`Kill switch error: ${message}`);
    },
  });
}

export function useRevokeAllCodeAgentSessions() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.post<ApiResponse<{ revokedCount: number }>>('/v1/admin/code-agent/kill-switches/revoke-all');
      return response.data;
    },
    onSuccess: (response) => {
      notification.success(`Revoked ${response.data?.revokedCount ?? 0} active sessions`);
      queryClient.invalidateQueries({ queryKey: ['code-agent-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['code-audit-logs'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      const message = error.response?.data?.message || error.message || 'Failed to revoke sessions';
      notification.error(`Revoke error: ${message}`);
    },
  });
}
